import { Spinner } from "@blueprintjs/core";
import { Link } from "react-router";

import { buildSessionUrl } from "@/components/pages_logged_in/roleplays/utils";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Card } from "@/components/ui/card";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { RoleplayRank, useRoleplaySessionsQuery } from "@/graphql";
import { formatDate } from "@/utils/datetime";

interface SessionHistoryDrawerProps {
  scenarioId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleplaySlug?: string;
}

export function SessionHistoryDrawer({
  scenarioId,
  open,
  onOpenChange,
  roleplaySlug,
}: SessionHistoryDrawerProps) {
  const { data, loading, error } = useRoleplaySessionsQuery({
    variables: {
      scenarioIds: [scenarioId],
    },
    skip: !open, // Don't fetch until drawer is opened
  });

  const sessions = data?.roleplaySessions ?? [];

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="w-3/4 sm:max-w-2xl">
        <DrawerCloseButton />
        <div className="flex flex-col h-full justify-between">
          <div className="flex flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 p-6">
              <DrawerTitle>Your Sessions</DrawerTitle>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              ) : error ? (
                <Card className="p-8 text-center text-red-600">
                  <p className="text-lg">Error loading sessions</p>
                  <p className="text-sm mt-2">{error.message}</p>
                </Card>
              ) : sessions.length === 0 ? (
                <Card className="p-8 text-center text-gray-500">
                  <p className="text-lg">No sessions... yet!</p>
                  <p className="text-sm mt-2">
                    Start a new session to see your history here.
                  </p>
                </Card>
              ) : (
                <div className="flex flex-col gap-3">
                  {sessions.map((session) => {
                    if (!session) return null;
                    const url = buildSessionUrl({
                      roleplaySlug,
                      scenarioSlug: session.scenario.slug,
                      sessionUuid: session.uuid,
                    });

                    return (
                      <Link
                        key={session.uuid}
                        to={url}
                        className="block transition-colors duration-200 text-gray-900 hover:text-inherit"
                      >
                        <div className="flex items-center bg-white justify-between p-4 border rounded-md hover:border-blue-700">
                          <div className="flex-1">
                            <div className="font-medium text-inherit">
                              {formatDate(
                                session.updatedAt,
                                "MMM do 'at' h:mm a",
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <RankIcon
                              rank={session.rank ?? RoleplayRank.Unranked}
                              size={24}
                            />
                            {session.score ? (
                              <span>{session.score}%</span>
                            ) : (
                              "Unranked"
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
